import { FC, useState } from 'react';
import styled from 'styled-components';
import type {
  TruckShareFacilityOutcomeDTO,
  TruckShareTargetDTO,
} from 'api/model';
import {
  useInternalDeliveryTruckShareGetTruckShareOverview,
  useInternalDeliveryTruckShareSetTruckShareTarget,
} from 'api/internal-delivery-truck-share/internal-delivery-truck-share';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import LabelWrap from 'components/inputs/LabelWrap';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { includesRoles, TruckShareTargetEditAccessPolicy } from 'constants/Roles';
import useMe from 'contexts/authentication/useMe';
import { formatDate, toInputDateString } from 'utils/date-helpers';
import { showException } from 'utils/exception-helper';

const Wrapper = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 900px;
`;

const Intro = styled.p`
  margin: 0;
  max-width: 75ch;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const Figure = styled.div`
  display: flex;
  flex-direction: column;
`;

const FigureValue = styled.span<{ tone?: 'below' | 'ok' }>`
  font-size: 22px;
  font-weight: 600;
  color: ${({ tone }) =>
    tone === 'below' ? 'rgb(150, 40, 40)' : tone === 'ok' ? 'rgb(20, 110, 60)' : 'inherit'};
`;

const FigureLabel = styled.span`
  font-size: 12px;
  color: #666;
`;

const SubHeading = styled.h3`
  margin: 6px 0 0;
  font-size: 14px;
`;

const Note = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0;
`;

const formatPercent = (value: number | null | undefined) =>
  value == null ? '—' : `${value.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} %`;

const describeTarget = (t: TruckShareTargetDTO) =>
  `${t.minTruckSharePercent} % från ${formatDate(new Date(t.validFrom))}${
    t.createdByName ? ` (satt av ${t.createdByName})` : ''
  }`;

const facilityColumns: ColumnSetting<TruckShareFacilityOutcomeDTO, object>[] = [
  { head: 'Anläggning', width: 220, render: (f) => f.name },
  { head: 'Annonsbilar', width: 90, render: (f) => f.totalCars },
  { head: 'På lastbil', width: 90, render: (f) => f.truckCars },
  { head: 'Andel lastbil', width: 100, render: (f) => formatPercent(f.truckPercent) },
];

/**
 * Riktvärdet "minst X % av annonsbilarna på lastbil" (beslut 2026-09-24). Ett globalt värde för
 * Riddermark-anläggningarna. AnnonsAdmin ser vyn; bara SuperAdmin kan ändra siffran — backend
 * spärrar skrivningen, låsta fält här är bara bekvämlighet.
 */
const TruckShareTargetPage: FC = () => {
  const me = useMe();
  const canEdit = includesRoles(TruckShareTargetEditAccessPolicy.allow, me?.roles);

  const overview = useInternalDeliveryTruckShareGetTruckShareOverview();
  const setTarget = useInternalDeliveryTruckShareSetTruckShareTarget();

  const [percent, setPercent] = useState<string>('');
  const [validFrom, setValidFrom] = useState<string>(toInputDateString(new Date()));

  const { data } = overview;
  const current = data?.current;
  const outcome = data?.outcome;
  const windowDays = outcome?.windowDays ?? 30;
  // Anläggningar utan annonsbilar i fönstret bidrar inte till totalen och döljs. Oftast är det
  // butiker som inte tar emot annonsbilar, eller Ride-skapade anläggningar med tom adress.
  const facilities = outcome?.facilities ?? [];
  const facilitiesWithCars = facilities.filter((f) => f.totalCars > 0);
  const hiddenFacilityCount = facilities.length - facilitiesWithCars.length;
  const parsedPercent = Number(percent);
  const percentIsValid =
    percent.trim() !== '' && Number.isInteger(parsedPercent) && parsedPercent >= 0 && parsedPercent <= 100;

  const save = async () => {
    if (!percentIsValid || !validFrom) return;
    try {
      await setTarget.mutateAsync({
        data: {
          minTruckSharePercent: parsedPercent,
          // Lokal midnatt: backend tolkar tidpunkten som det svenska datumet den infaller på.
          validFrom: new Date(`${validFrom}T00:00:00`),
        },
      });
      setPercent('');
      await overview.refetch();
    } catch (error) {
      showException(error);
    }
  };

  return (
    <Wrapper>
      <Intro>
        Riktvärdet anger hur stor andel av annonsbilarna till Riddermark-anläggningarna som minst
        ska gå på lastbil — egen lastbil eller inhyrd transportör. Ligger utfallet under
        riktvärdet märker ruttplaneringen annonsförfrågningar dit och förväljer dem inte, eftersom
        en fälttestare då tar en plats som en lastbil kunde ha fyllt. Riktvärdet är ingen spärr:
        förfrågan går fortfarande att välja.
      </Intro>

      <Row>
        <Figure>
          <FigureValue>{current ? `${current.minTruckSharePercent} %` : 'Ej satt'}</FigureValue>
          <FigureLabel>Riktvärde (minst)</FigureLabel>
        </Figure>
        <Figure>
          <FigureValue
            tone={
              outcome?.isBelowTarget == null ? undefined : outcome.isBelowTarget ? 'below' : 'ok'
            }
          >
            {formatPercent(outcome?.truckPercent)}
          </FigureValue>
          <FigureLabel>
            Utfall senaste {windowDays} dygn ({outcome?.truckCars ?? 0} av{' '}
            {outcome?.totalCars ?? 0} bilar)
          </FigureLabel>
        </Figure>
      </Row>
      {current && <Note>Gäller: {describeTarget(current)}</Note>}
      {(data?.upcoming.length ?? 0) > 0 && (
        <Note>Kommande: {data?.upcoming.map(describeTarget).join(' · ')}</Note>
      )}

      <SubHeading>Ändra riktvärdet</SubHeading>
      {!canEdit && <Note>Bara superadmin kan ändra riktvärdet.</Note>}
      <Row>
        <LabelWrap label="Minst andel på lastbil (%)">
          <Input
            disabled={!canEdit}
            max={100}
            min={0}
            onChange={(e) => setPercent(e.target.value)}
            placeholder={current ? String(current.minTruckSharePercent) : 't.ex. 40'}
            type="number"
            value={percent}
            width={120}
          />
        </LabelWrap>
        <LabelWrap label="Gäller från">
          <Input
            disabled={!canEdit}
            // Bakåt nekas av backend: historiken ska visa vad som faktiskt gällde.
            min={toInputDateString(new Date())}
            onChange={(e) => setValidFrom(e.target.value)}
            type="date"
            value={validFrom}
            width={150}
          />
        </LabelWrap>
        <Button disabled={!canEdit || !percentIsValid || setTarget.isLoading} onClick={save}>
          Spara
        </Button>
      </Row>
      <Note>
        Ett nytt värde gäller från i dag eller ett senare datum — aldrig bakåt. Samma datum som ett
        kommande värde ersätter det. Tidigare värden sparas, så att utfallet kan jämföras mot det
        riktvärde som gällde då.
      </Note>

      <SubHeading>Utfall per anläggning</SubHeading>
      <Note>
        Bara information — riktvärdet jämförs mot totalen ovan. Omfattar aktiva anläggningar
        markerade som Riddermark, och räknas på samma sätt som ledningsrapportens
        &quot;Annonsbilar på lastbil&quot;.
      </Note>
      {facilitiesWithCars.length > 0 ? (
        <>
          <Table fluid columnSettings={facilityColumns} rows={facilitiesWithCars} />
          {hiddenFacilityCount > 0 && (
            <Note>
              {hiddenFacilityCount === 1
                ? '1 Riddermark-anläggning'
                : `${hiddenFacilityCount} Riddermark-anläggningar`}{' '}
              utan annonsbilar senaste {windowDays} dygnen visas inte.
            </Note>
          )}
        </>
      ) : (
        <Note>Inga annonsbilar till Riddermark-anläggningar senaste {windowDays} dygnen.</Note>
      )}

      {(data?.history.length ?? 0) > 0 && (
        <>
          <SubHeading>Tidigare riktvärden</SubHeading>
          <Note>{data?.history.map(describeTarget).join(' · ')}</Note>
        </>
      )}
    </Wrapper>
  );
};

export default TruckShareTargetPage;
